import { Fragment, memo, useMemo, useState } from 'react';
import { Tooltip, useEventCallback } from '@mui/material';
import Table from 'shell/components/shared/table';
import { SearchPersonTableVM } from 'civil-status/view-models/accordion-generality-form';
import { IconButton } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { noop } from 'shell/utils/noop';
import { HeadCell } from 'shell/components/shared/table/types';
import { SearchPersonVM } from 'civil-status/view-models/step-form';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import { CivilStatusID } from 'shell/constants/civil-status';

const headCells: HeadCell<SearchPersonTableVM, keyof SearchPersonTableVM>[] = [
  {
    id: 'fiscalCode',
    label: 'Codice Fiscale',
  },
  {
    id: 'surname',
    label: 'Cognome',
  },
  {
    id: 'name',
    label: 'Nome',
  },
  {
    id: 'gender',
    label: 'Sesso',
  },
  {
    id: 'dateBorn',
    label: 'Data di nascita',
  },
  {
    id: 'placeBorn',
    label: 'Luogo di nascita',
  },
  {
    id: 'residence',
    label: 'Residenza',
  },
  {
    id: 'state',
    label: 'Stato',
  },
  {
    id: 'action',
    label: 'Azioni',
  },
];

const headCellsAnsc: HeadCell<SearchPersonTableVM, keyof SearchPersonTableVM>[] = [
  {
    id: 'fiscalCode',
    label: 'Codice Fiscale',
  },
  {
    id: 'surname',
    label: 'Cognome',
  },
  {
    id: 'name',
    label: 'Nome',
  },
  {
    id: 'gender',
    label: 'Sesso',
  },
  {
    id: 'dateBorn',
    label: 'Data di nascita',
  },
  {
    id: 'placeBorn',
    label: 'Luogo di nascita',
  },
  {
    id: 'residence',
    label: 'Residenza',
  },
  {
    id: 'idAnsc',
    label: 'ID Ansc Soggetto',
  },
  {
    id: 'action',
    label: 'Azioni',
  },
];

interface TableSearchPersonProps {
  tableBody?: Array<SearchPersonTableVM>;
  onSearchPerson: (valuesSearchPerson?: SearchPersonVM, withTipoUseCase?: boolean) => void;
  onVisibilityPressAnsc: (valuesSearchPerson?: SearchPersonTableVM) => void;
  isAnsc?: boolean;
  isBaseDec?: boolean;
  configuration?: string;
}

const TableSearchPersonComponent = ({
  tableBody = [],
  onSearchPerson,
  onVisibilityPressAnsc,
  isAnsc = false,
  isBaseDec = false,
  configuration,
}: TableSearchPersonProps): React.ReactElement => {
  const [tableProps, setTableProps] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  const onChangePage = useEventCallback((page: number) => {
    setTableProps({
      rowsPerPage: tableProps.rowsPerPage,
      page,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setTableProps({
      rowsPerPage,
      page: 0,
    });
  });

  const onVisibilityPress = useEventCallback((item: SearchPersonTableVM) => {
    if (isAnsc) {
      onVisibilityPressAnsc(item);
      return;
    }

    // selezionando un soggetto dalla tabella si avvia la seconda chiamata (con tipoUseCase)
    // per recuperare la famiglia e valorizzare gli accordion padre e madre
    onSearchPerson(
      {
        fiscalCode: item.fiscalCode,
        idAnpr: item.idAnpr,
        name: item.name,
        surname: item.surname,
      },
      true
    );
  });

  const body: Array<SearchPersonTableVM> = useMemo(() => {
    if (tableBody?.length === 0) {
      return [];
    }

    return tableBody?.map((item) => {
      let messageAction = '';

      const civilStatus = item.remoteData?.statoCivile || ('' as CivilStatusID);

      // Se coniugato o unito civilmente e casi d'uso neosnoint, neosnointp, peric_civ, neospos
      // fa vedere warning
      if (
        (configuration === ConfigurationType.NEOSNOINT ||
          configuration === ConfigurationType.NEOSNOINTP ||
          configuration === ConfigurationType.PERIC_CIV ||
          configuration === ConfigurationType.NEOSPOS) &&
        (civilStatus === CivilStatusID.coniugato || civilStatus === CivilStatusID.unitoCivilmente)
      ) {
        messageAction = `${messageAction} Il soggetto è già coniugato o unito civilmente\n`;
      }

      // Se deceduto
      // fa vedere warning
      if (item.motivoCancellazione === '1' && isBaseDec) {
        messageAction = `${messageAction} Il soggetto è deceduto`;
      }

      return {
        ...item,
        action: (
          <Tooltip title={messageAction} arrow>
            <IconButton
              aria-label="visualizza"
              component="span"
              onClick={() => onVisibilityPress(item)}
              disabled={!!messageAction}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
        ),
      };
    });
  }, [tableBody, onVisibilityPress]);

  return (
    <Fragment>
      {body.length > 0 ? (
        <Table
          ariaLabel="Lista Persone"
          page={tableProps.page}
          rowsPerPage={tableProps.rowsPerPage}
          body={body}
          count={body?.length || 0}
          head={isAnsc ? headCellsAnsc : headCells}
          onSort={noop}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          orders={{}}
          isNotLayoutFixed
          noSort
          hideButtons
        />
      ) : undefined}
    </Fragment>
  );
};

TableSearchPersonComponent.displayName = 'table search person';

const TableSearchPerson = memo(TableSearchPersonComponent);

export default TableSearchPerson;
